/**
 * ch02's translation between the pinned client and the pre-season screens.
 *
 * The same three labels the foundations chapter's mapping module uses, for the same
 * reason — a mapping that is not one of the three is a mapping nobody can audit:
 *
 *   TRANSFORM  a shape change that loses nothing
 *   DERIVE     a value computed from what the contract does carry, stated as such
 *   GAP        a field a screen (or a request) requires that the contract does not expose
 *
 * ── The buy diagnosis, after ADR 0104 ────────────────────────────────────────────────
 *
 * `POST /api/v1/preseason/buy/diagnose` no longer takes `newsvendorUnits`. The platform's
 * own note on `BuyCandidateInput` is worth reading in full, but the operative sentences
 * are these: the depth "is now read from the stored economics and the stored quantile
 * forecast", `substitutionRate` "has no default", and the model is `extra="forbid"` so a
 * client still sending the old field gets a **422** rather than a silently different
 * answer.
 *
 * That makes assembling the request the interesting problem, and it is the reason this
 * module exists rather than the screen building a body inline. Nine things go into a
 * diagnosis: six published reads, two effective-dated parameters and the period the planner
 * is looking at.
 *
 * | Request field | Where it comes from | Kind |
 * |---|---|---|
 * | `candidates[].candidateId/optionId/clusterId/supplierKey/receiptPeriodKey/unitCost` | `GET /preseason/buy-lines` | TRANSFORM |
 * | `assignment` | the same buy lines' `quantityUnits` | TRANSFORM |
 * | `supplierCapacityUnits` | `GET /strategic/reservations`, `remainingUnits`, keyed `supplier|period` | DERIVE |
 * | `candidates[].substitutionRate` | `GET /foundations/parameters` — G1: "every tunable is here or nowhere" | DERIVE |
 * | `forecastModelVersionId`, `objectiveConfigId`, `programId` | the same registry | DERIVE |
 * | `forecastPeriod` | the receipt period the screen is looking at (`?period=`) | context |
 * | `candidates[].marginPerUnit` | `GET /preseason/option-economics`, per option × cluster | TRANSFORM |
 * | `otbBudgetByPeriod` | `GET /preseason/period-plans`, sent unchanged to `POST /preseason/otb`; the period's `openToBuyUnits` | DERIVE |
 *
 * Nothing here invents an input. `buildDiagnosis` returns either a complete request or the
 * list of what is missing, and the screen renders the list. A diagnosis run against an
 * invented margin or an invented budget would return a *depth* — a number a planner would
 * order against — derived from a figure this repository made up, which is the precise
 * failure G2.2 exists to prevent and a far worse outcome than a panel that says it cannot
 * run yet. A read that answers empty is a gap that names the data not yet stored.
 *
 * **The OTB budget is one node's.** `otbBudgetByPeriod` carries no node and a buy line names
 * none, so the budget is taken only when exactly one plan node covers the period. Several
 * nodes are a stated gap rather than a sum: nodes may nest (a total and its classes), and
 * adding them would count one budget twice.
 *
 * The `substitutionRate` lookup deserves its own note. ADR 0104 removed the API default
 * because "a defaulted substitution rate would be a planning parameter nobody could see
 * or date". Defaulting it *here* would reintroduce exactly that, one layer up, so a
 * candidate whose cluster has no registered rate is a MISSING INPUT and not a 0.
 */

import { ApiProblemError } from '@/api';
import type {
  BuyCandidateInput,
  BuyDiagnoseRequest,
  BuyDepthOut,
  BuyLineSummary,
  OptionEconomicsSummary,
  OtbResponse,
  ParameterSummary,
  PeriodPlanInput,
  PeriodPlanTerms,
  ReservationRow,
} from '@/api/generated';

// ── named refusals and designed states ───────────────────────────────────────

/**
 * The refusal ADR 0104's derivation can raise: the stored quantile levels do not bracket
 * the critical ratio.
 *
 * It is NOT an error and it is NOT something to clamp. The critical ratio comes from the
 * unit economics and the stored levels come from the forecast configuration, so a ratio
 * outside the stored bracket is a statement about the two together: either the economics
 * moved or the forecast was not stored at a level fine enough to price them. Clamping to
 * the nearest stored level would answer a question nobody asked — it would return a depth
 * for a *different* critical ratio and label it as this one's.
 *
 * The code is not in `KNOWN_PROBLEM_CODES` (that list is the platform's transport-level
 * vocabulary in `services/api/errors.py`), so `problemMessage` would render it through
 * `api.problem.unknown` with the service's own detail. That is a correct fallback and a
 * poor screen: this refusal has a specific thing to say to a merchant, and it says it in
 * this chapter's catalogue.
 */
export const QUANTILE_LEVEL_NOT_STORED = 'planning.quantile.level_not_stored';

export function isQuantileLevelNotStored(error: unknown): boolean {
  return error instanceof ApiProblemError && error.problem.code === QUANTILE_LEVEL_NOT_STORED;
}

/**
 * The diagnosis's refusals over the suppliers' terms (contract audit B6, bata-platform
 * `fix/preseason-wave1`): the minimum order and the pack are checked from the suppliers' terms,
 * and the platform refuses rather than reporting feasible a buy whose minimum it could not check.
 * Named here, as the quantile refusal is, because each has a specific thing to say to a merchant.
 */
export const SUPPLIER_TERMS_UNAVAILABLE = 'planning.buy.supplier_terms_unavailable';
export const SUPPLIER_TERMS_MISSING = 'planning.buy.supplier_terms_missing';

export function isProblemCode(error: unknown, code: string): boolean {
  return error instanceof ApiProblemError && error.problem.code === code;
}

/**
 * 401 specifically, and not 403.
 *
 * `isPermissionProblem` covers both, which is right for a table that a planner may not be
 * allowed to see. The diagnosis needs the narrower question: a derived depth reads the
 * estate, so the endpoint now refuses an anonymous caller outright. "Sign in" and "your
 * role does not include this" are different sentences and lead to different actions.
 */
export function isIdentityRequired(error: unknown): boolean {
  return error instanceof ApiProblemError && error.status === 401;
}

// ── the effective-dated parameter registry ───────────────────────────────────

/**
 * The parameter keys the diagnosis reads.
 *
 * G1 puts every tunable in the registry "or nowhere", and G9 requires a published number
 * to re-derive from a named parameter set — which is why `BuyDiagnoseRequest` carries the
 * forecast identifiers rather than resolving them server-side ("a model version the
 * caller cannot see is one they cannot cite"). The registry is the only published place
 * a caller can read them from, so it is where this module looks. If the platform later
 * publishes a buy-context read, that is the better source and this indirection goes.
 */
export const DIAGNOSIS_PARAMETER_KEYS = {
  programId: 'preseason.buy.program_id',
  objectiveConfigId: 'preseason.buy.objective_config_id',
  forecastModelVersionId: 'preseason.buy.forecast_model_version_id',
  substitutionRate: 'preseason.buy.substitution_rate',
} as const;

export interface ParameterScope {
  readonly dimension: string;
  readonly value: string;
}

/** Scope order is most-specific-first; the first match wins and no match is `undefined`. */
export function findParameter(
  parameters: readonly ParameterSummary[],
  parameterKey: string,
  scopes: readonly ParameterScope[],
): ParameterSummary | undefined {
  for (const scope of scopes) {
    const hit = parameters.find(
      (parameter) => parameter.parameterKey === parameterKey
        && parameter.scopeDimension === scope.dimension
        && parameter.scopeValue === scope.value,
    );
    if (hit) return hit;
  }
  return undefined;
}

/** The scope every parameter may be registered at when it applies to the whole estate. */
export const GLOBAL_SCOPE: ParameterScope = { dimension: 'global', value: 'all' };

// ── assembling a diagnosis ───────────────────────────────────────────────────

/**
 * One input the diagnosis needs and does not have.
 *
 * `kind` separates the failures, because they have different owners and different fixes:
 *
 *   `unregistered`  a parameter with no row in the registry — closed by whoever registers it;
 *   `not-stored`    data the platform reads and has not received (no margin for the option,
 *                   no plan for the period, no booked capacity) — closed on the data side;
 *   `ambiguous`     the data exists but does not say which of several rows applies — closed
 *                   by narrowing the plan, never by this page picking one.
 */
export interface DiagnosisGap {
  readonly id: string;
  readonly kind: 'unregistered' | 'not-stored' | 'ambiguous';
  readonly labelKey: string;
  /**
   * What the gap is about, in the platform's references (an option and its cluster, a supplier
   * and a period). Reference data, never prose (G6); `null` when the label says it all, as for a
   * parameter the label names in words.
   */
  readonly reference: GapReference | null;
}

/** The subject of a gap, so the screen can say it in words (a period, a supplier) rather than as a key. */
export type GapReference =
  | { readonly kind: 'period'; readonly period: string }
  | { readonly kind: 'cluster'; readonly clusterId: string }
  | { readonly kind: 'supplier'; readonly supplierKey: string; readonly period: string }
  | { readonly kind: 'option'; readonly optionId: string; readonly clusterId: string }
  | { readonly kind: 'nodes'; readonly nodes: readonly string[] };

export type Diagnosis =
  | { readonly runnable: true; readonly request: BuyDiagnoseRequest }
  | { readonly runnable: false; readonly gaps: readonly DiagnosisGap[] };

/**
 * The period's open-to-buy budget, or why there is none.
 *
 *   `budget`         the plan's `openToBuyUnits` for the period, as `POST /preseason/otb` computed it;
 *   `no-plan`        no stored plan covers the period;
 *   `several-nodes`  more than one plan node covers it, and nothing says which one the buy draws on;
 *   `not-in-ledger`  the ledger answered without the period.
 */
export type OtbBudgetReading =
  | { readonly kind: 'budget'; readonly units: number; readonly nodeId: string }
  | { readonly kind: 'no-plan' }
  | { readonly kind: 'several-nodes'; readonly nodes: readonly string[] }
  | { readonly kind: 'not-in-ledger'; readonly nodeId: string };

/** Which stored plan a period's budget comes from — or why none does. */
export type PeriodBudgetSource =
  | { readonly kind: 'single'; readonly nodeId: string; readonly periods: readonly PeriodPlanInput[] }
  | { readonly kind: 'no-plan' }
  | { readonly kind: 'several-nodes'; readonly nodes: readonly string[] };

/**
 * DERIVE — which stored plan the period's budget comes from.
 *
 * `single`: exactly one node plans the period; `periods` is that node's EVERY period, sent to
 * `POST /preseason/otb` unchanged (the read's own note: "these terms go back … unchanged").
 * Otherwise the reading says why there is no budget. Pure.
 */
export function periodBudgetSource(plans: readonly PeriodPlanTerms[], period: string): PeriodBudgetSource {
  const nodes = [...new Set(plans.filter((plan) => plan.period === period).map((plan) => plan.nodeId))].sort();
  if (nodes.length === 0) return { kind: 'no-plan' };
  if (nodes.length > 1) return { kind: 'several-nodes', nodes };
  const nodeId = nodes[0] as string;
  return { kind: 'single', nodeId, periods: plansOfNode(plans, nodeId) };
}

/** TRANSFORM — one node's stored plan rows as `POST /preseason/otb` takes them: the same terms, without the node. */
export function plansOfNode(plans: readonly PeriodPlanTerms[], nodeId: string): readonly PeriodPlanInput[] {
  return plans.filter((plan) => plan.nodeId === nodeId).map((plan) => ({
    period: plan.period,
    openingInventory: plan.openingInventory,
    closingInventory: plan.closingInventory,
    plannedSalesUnits: plan.plannedSalesUnits,
    plannedMarkdownUnits: plan.plannedMarkdownUnits,
    plannedShrinkUnits: plan.plannedShrinkUnits,
    onOrderUnits: plan.onOrderUnits,
  }));
}

/** DERIVE — the period's budget out of the ledger `POST /preseason/otb` returned for its node. Pure. */
export function otbBudgetFrom(ledger: OtbResponse, period: string): OtbBudgetReading {
  const position = ledger.positions.find((entry) => entry.period === period);
  return position === undefined
    ? { kind: 'not-in-ledger', nodeId: ledger.nodeId }
    : { kind: 'budget', units: position.openToBuyUnits, nodeId: ledger.nodeId };
}

/**
 * The supplier-capacity key the diagnosis reads: `supplier|period`. The platform splits the key on
 * `|` and checks capacity per supplier AND period, so a key without its period matches no
 * candidate and checks nothing.
 */
export function capacityKey(supplierKey: string, period: string): string {
  return `${supplierKey}|${period}`;
}

export interface DiagnosisInputs {
  /** The buy lines the planner is looking at — already narrowed to one receipt period. */
  readonly lines: readonly BuyLineSummary[];
  readonly parameters: readonly ParameterSummary[];
  readonly reservations: readonly ReservationRow[];
  /** The receipt period, taken from the screen's own context rather than defaulted. */
  readonly forecastPeriod: string;
  /** The stored unit economics, per option × cluster (`GET /preseason/option-economics`). */
  readonly economics: readonly OptionEconomicsSummary[];
  /** The period's open-to-buy budget, or why there is none. */
  readonly otbBudget: OtbBudgetReading;
}

/** The gap an absent budget becomes. */
function budgetGap(reading: Exclude<OtbBudgetReading, { kind: 'budget' }>, period: string): DiagnosisGap {
  switch (reading.kind) {
    case 'no-plan':
      return { id: 'otbBudget', kind: 'not-stored', labelKey: 'preseason.diagnosis.gap.otbBudget', reference: { kind: 'period', period } };
    case 'not-in-ledger':
      return { id: 'otbBudget', kind: 'not-stored', labelKey: 'preseason.diagnosis.gap.otbBudgetNotInLedger', reference: { kind: 'period', period } };
    case 'several-nodes':
      return { id: 'otbBudget', kind: 'ambiguous', labelKey: 'preseason.diagnosis.gap.otbBudgetSeveralNodes', reference: { kind: 'nodes', nodes: reading.nodes } };
  }
}

/**
 * TRANSFORM + DERIVE + GAP — the buy lines of one receipt period into a diagnosis.
 *
 * Returns the request or the reasons it cannot be built. There is no third outcome and,
 * in particular, no partially-assembled request: an incomplete body would be rejected by
 * `extra="forbid"`'s sibling validation as a 422 and a planner would read a validation
 * failure where the true finding is the input that has not been stored yet.
 */
export function buildDiagnosis(inputs: DiagnosisInputs): Diagnosis {
  const {
    lines, parameters, reservations, forecastPeriod, economics, otbBudget,
  } = inputs;
  const gaps: DiagnosisGap[] = [];
  const seen = new Set<string>();
  const gap = (entry: DiagnosisGap): void => {
    if (seen.has(entry.id)) return;
    seen.add(entry.id);
    gaps.push(entry);
  };

  const registered = (field: keyof typeof DIAGNOSIS_PARAMETER_KEYS, labelKey: string): string => {
    const parameterKey = DIAGNOSIS_PARAMETER_KEYS[field];
    const hit = findParameter(parameters, parameterKey, [GLOBAL_SCOPE]);
    const value = hit?.textValue;
    if (typeof value !== 'string' || value === '') {
      gap({ id: parameterKey, kind: 'unregistered', labelKey, reference: null });
      return '';
    }
    return value;
  };

  const programId = registered('programId', 'preseason.diagnosis.gap.programId');
  const objectiveConfigId = registered(
    'objectiveConfigId', 'preseason.diagnosis.gap.objectiveConfig',
  );
  const forecastModelVersionId = registered(
    'forecastModelVersionId', 'preseason.diagnosis.gap.forecastModelVersion',
  );

  // The budget is the stored plan's, through the platform's one ledger arithmetic — never
  // derived from anything else on this screen, which would be inventing the constraint the
  // diagnosis is being asked to test.
  if (otbBudget.kind !== 'budget') gap(budgetGap(otbBudget, forecastPeriod));

  const candidates: BuyCandidateInput[] = [];
  const assignment: Record<string, number> = {};
  const supplierCapacityUnits: Record<string, number> = {};

  for (const line of lines) {
    const rate = findParameter(parameters, DIAGNOSIS_PARAMETER_KEYS.substitutionRate, [
      { dimension: 'option', value: line.sourceOptionId },
      { dimension: 'cluster', value: line.clusterId },
    ])?.numericValue;
    if (typeof rate !== 'number') {
      // No default. ADR 0104 removed the API's default so the rate could not be a
      // parameter nobody can see or date; supplying one here would restore it.
      gap({
        id: `${DIAGNOSIS_PARAMETER_KEYS.substitutionRate}:${line.clusterId}`,
        kind: 'unregistered',
        labelKey: 'preseason.diagnosis.gap.substitutionRate',
        reference: { kind: 'cluster', clusterId: line.clusterId },
      });
      continue;
    }

    const reservation = reservations.find(
      (row) => row.supplierKey === line.supplierKey && row.periodKey === line.receiptPeriodKey,
    );
    if (reservation === undefined) {
      gap({
        id: `capacity:${line.supplierKey}:${line.receiptPeriodKey}`,
        kind: 'not-stored',
        labelKey: 'preseason.diagnosis.gap.supplierCapacity',
        reference: { kind: 'supplier', supplierKey: line.supplierKey, period: line.receiptPeriodKey },
      });
      continue;
    }

    // The margin is the option's in THIS cluster: the economics are stored per option ×
    // cluster, and a price or a markdown realisation differs between clusters.
    const economicsRow = economics.find(
      (row) => row.optionId === line.sourceOptionId && row.clusterId === line.clusterId,
    );
    if (economicsRow === undefined) {
      gap({
        id: `margin:${line.sourceOptionId}:${line.clusterId}`,
        kind: 'not-stored',
        labelKey: 'preseason.diagnosis.gap.marginPerUnit',
        reference: { kind: 'option', optionId: line.sourceOptionId, clusterId: line.clusterId },
      });
      continue;
    }
    // A margin and a unit cost in two currencies cannot meet in one critical ratio; converting
    // here would pick an exchange rate nobody named.
    if (economicsRow.currencyCode !== line.currencyCode) {
      gap({
        id: `margin-currency:${line.sourceOptionId}:${line.clusterId}`,
        kind: 'ambiguous',
        labelKey: 'preseason.diagnosis.gap.marginCurrency',
        reference: { kind: 'option', optionId: line.sourceOptionId, clusterId: line.clusterId },
      });
      continue;
    }

    supplierCapacityUnits[capacityKey(line.supplierKey, line.receiptPeriodKey)] = reservation.remainingUnits;

    candidates.push({
      candidateId: line.buyLineId,
      clusterId: line.clusterId,
      optionId: line.sourceOptionId,
      receiptPeriodKey: line.receiptPeriodKey,
      supplierKey: line.supplierKey,
      unitCost: line.unitCostAmount,
      substitutionRate: rate,
      marginPerUnit: economicsRow.marginPerUnit,
    });
    assignment[line.buyLineId] = line.quantityUnits;
  }

  if (candidates.length === 0) {
    gap({
      id: 'candidates',
      kind: 'not-stored',
      labelKey: 'preseason.diagnosis.gap.candidates',
      reference: { kind: 'period', period: forecastPeriod },
    });
  }

  if (gaps.length > 0 || otbBudget.kind !== 'budget') return { runnable: false, gaps };

  return {
    runnable: true,
    request: {
      assignment,
      candidates,
      forecastModelVersionId,
      forecastPeriod,
      objectiveConfigId,
      otbBudgetByPeriod: { [forecastPeriod]: otbBudget.units },
      programId,
      supplierCapacityUnits,
    },
  };
}

// ── reading the response ─────────────────────────────────────────────────────

/**
 * The closed vocabularies this chapter resolves at the call site.
 *
 * Literal maps rather than an interpolated key, because both are closed sets on the
 * platform side and a value outside them should be visible as unclassified rather than
 * rendered as a raw identifier — and because an interpolated key is one the i18n coverage
 * gate cannot see (its dynamic-key registry lives in `tools/`, which this chapter does
 * not edit).
 */
export const COST_PROVENANCE_LABEL_KEY: Readonly<Record<string, string>> = {
  actual: 'preseason.costProvenance.actual',
  standard: 'preseason.costProvenance.standard',
  modelled: 'preseason.costProvenance.modelled',
};

export const UNCLASSIFIED_COST_PROVENANCE_KEY = 'preseason.costProvenance.unclassified';

/**
 * DERIVE — cost provenance into a label key and a status level.
 *
 * reqs/07 R3 requires a modelled cost to be distinguishable on every output derived from
 * it, and `BuyDepthOut` carries the provenance on the row for exactly that reason. An
 * unrecognised provenance is `warning` and labelled unclassified: silently showing it as
 * an actual cost is the one reading that would let a modelled number pass as measured.
 */
export function costProvenanceLabelKey(provenance: string): string {
  return COST_PROVENANCE_LABEL_KEY[provenance] ?? UNCLASSIFIED_COST_PROVENANCE_KEY;
}

export function costProvenanceIsMeasured(provenance: string): boolean {
  return provenance === 'actual';
}

export const BINDING_LABEL_KEY: Readonly<Record<string, string>> = {
  otb_budget: 'preseason.binding.otbBudget',
  supplier_capacity: 'preseason.binding.supplierCapacity',
  minimum_order_quantity: 'preseason.binding.minimumOrderQuantity',
  lead_time: 'preseason.binding.leadTime',
  presentation_floor: 'preseason.binding.presentationFloor',
  depth_ceiling: 'preseason.binding.depthCeiling',
};

export const UNCLASSIFIED_BINDING_KEY = 'preseason.binding.unclassified';

export function bindingLabelKey(reason: string): string {
  return BINDING_LABEL_KEY[reason] ?? UNCLASSIFIED_BINDING_KEY;
}

/**
 * DERIVE — the depth a candidate was assigned against the depth the economics imply.
 *
 * `BuyDepthOut` publishes `quantileUnits` (the stored forecast at the critical ratio) and
 * `newsvendorUnits` (that depth net of substitution, where `netOfSubstitution` says so).
 * The assignment is what the planner proposed. The difference is the finding, and it is
 * returned as `null` rather than `0` when the candidate is not in the assignment: an
 * unassigned candidate has no variance, which is not the same as a variance of nothing.
 */
export function depthVariance(
  depth: BuyDepthOut,
  assignment: Readonly<Record<string, number>>,
): number | null {
  const assigned = assignment[depth.candidateId];
  return typeof assigned === 'number' ? assigned - depth.newsvendorUnits : null;
}

// ── cluster schemes ──────────────────────────────────────────────────────────

// The scheme version a page reads membership for is `shownScheme` in `./screens/ClusterScreen.tsx`:
// the version the address names, else the current refresh of the first scheme
// (`isLatestRefresh`, `./readers.ts`). The first unfrozen row it replaced was the OLDEST refresh.

/**
 * TRANSFORM — membership rows into a store count per cluster.
 *
 * A count is not a measurement of anything except the rows the API returned, which is why
 * it is derived here rather than read: `ClusterSchemeSummary.storeCount` is the scheme's
 * own count and it is shown as such; this is the per-cluster split of the rows on screen.
 */
export function storesPerCluster(
  rows: readonly { readonly clusterId: string; readonly storeKey: string }[],
): readonly { readonly clusterId: string; readonly storeCount: number }[] {
  const counts = new Map<string, Set<string>>();
  for (const row of rows) {
    const seen = counts.get(row.clusterId) ?? new Set<string>();
    seen.add(row.storeKey);
    counts.set(row.clusterId, seen);
  }
  return [...counts.entries()]
    .map(([clusterId, stores]) => ({ clusterId, storeCount: stores.size }))
    .sort((left, right) => left.clusterId.localeCompare(right.clusterId));
}

// ── size curves ──────────────────────────────────────────────────────────────

/**
 * The published curve a screen should read.
 *
 * `SizeCurveSummary.clusterId` is nullable — a curve resolved at category level has no
 * cluster — so "the curve for this cluster" prefers an exact match and falls back to the
 * category-level curve, which is what `resolvedLevel` is telling the reader anyway.
 */
export function curveForCluster<T extends { readonly clusterId?: string | null }>(
  curves: readonly T[],
  clusterId: string,
): T | undefined {
  return curves.find((curve) => curve.clusterId === clusterId) ?? curves[0];
}
